import type { DataOption } from '@/common.types'
import type {
	FieldOption,
	UpdateProductsByFieldsSchema,
} from '../../model/types'

import { PlusIcon, XIcon } from 'lucide-react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Combobox from '@/shared/components/query-builder/components/Combobox'
import { Button } from '@/shared/components/ui/button'
import {
	Field,
	FieldContent,
	FieldError,
	FieldGroup,
	FieldLegend,
	FieldSeparator,
	FieldSet,
} from '@/shared/components/ui/field'

import { getFieldEditor } from '../../lib/get-field-editor'

export const FieldsList = ({
	fields,
	getField,
	getFieldOptions,
}: {
	fields: FieldOption[]
	getField: (value: string) => FieldOption | undefined
	getFieldOptions: (field: FieldOption) => DataOption[]
}) => {
	const { t } = useTranslation()

	const methods = useFormContext<UpdateProductsByFieldsSchema>()
	const {
		fields: fieldsArray,
		append,
		remove,
	} = useFieldArray({
		name: 'fields',
	})

	return (
		<FieldSet className='gap-4'>
			<FieldLegend variant='label'>
				{t('bulk_modal_bulk_update.fields_description', {
					defaultValue: 'Select the fields you want to update.',
				})}
			</FieldLegend>
			<FieldGroup className='gap-2'>
				{fieldsArray.map((f, index) => (
					<div key={f.id} className='flex w-full gap-2'>
						<Controller
							name={`fields.${index}.value`}
							render={({ field, fieldState }) => (
								<Field
									className='w-1/3 shrink-0'
									orientation='horizontal'
									data-invalid={fieldState.invalid}
								>
									<FieldContent>
										<Combobox
											{...field}
											data-testid='products-bulk-update-modal-field-combobox'
											invalid={fieldState.invalid}
											className='w-full'
											placeholder={t('general_search')}
											options={fields || []}
										/>
										<FieldError
											errors={[{ message: fieldState.error?.message }]}
										/>
									</FieldContent>
								</Field>
							)}
						/>
						<Controller
							name={`fields.${index}.inputValue`}
							render={({ field, fieldState }) => {
								const fieldOption = getField(
									methods.watch(`fields.${index}.value`)
								)
								if (!fieldOption) return <div className='w-full' />

								return (
									<Field
										className='col-span-7'
										orientation='horizontal'
										data-invalid={fieldState.invalid}
									>
										<FieldContent>
											{getFieldEditor(
												{
													field: fieldOption,
													options: getFieldOptions(fieldOption),
													value: field.value,
													onChange: field.onChange,
													invalid: fieldState.invalid,
												},
												t
											)}
											<FieldError
												errors={[{ message: fieldState.error?.message }]}
											/>
										</FieldContent>
									</Field>
								)
							}}
						/>
						<Button
							variant='outline'
							onClick={() => remove(index)}
							size='icon'
							data-testid='products-bulk-update-modal-remove-field-button'
						>
							<XIcon />
						</Button>
					</div>
				))}
				<FieldError
					errors={[{ message: methods.formState.errors.fields?.message }]}
				/>
			</FieldGroup>
			<FieldSeparator />
			<FieldGroup className='items-start'>
				{fieldsArray.length <= (fields?.length || 0) ? (
					<Button
						data-testid='products-bulk-update-modal-add-field-button'
						variant='ghost'
						onClick={() =>
							append({
								value: '',
								inputValue: '',
							})
						}
					>
						<PlusIcon />
						{t('bulk_modal_add_field')}
					</Button>
				) : null}
			</FieldGroup>
		</FieldSet>
	)
}
